import React, { useState, useRef, useEffect } from 'react';

const OptimizedImage = ({
  src,
  alt,
  className = '',
  wrapperClassName = '',
  width,
  height,
  aspectRatio,
  srcSet,
  sizes,
  objectFit = 'cover',
  priority = false,
  onLoad: onLoadProp,
  onClick,
  style,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const imgRef = useRef(null);

  useEffect(() => {
    if (priority) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px', threshold: 0.01 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  const handleLoad = () => {
    setIsLoaded(true);
    onLoadProp?.();
  };

  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && !alt) {
      console.warn('OptimizedImage: alt text is required for accessibility');
    }
  }, [alt]);

  const wrapperStyle = {};
  if (width) wrapperStyle.width = width;
  if (height) wrapperStyle.height = height;
  if (aspectRatio) wrapperStyle.aspectRatio = aspectRatio;
  else if (!width && !height) wrapperStyle.aspectRatio = '16/9';

  return (
    <div
      ref={imgRef}
      className={`relative overflow-hidden ${wrapperClassName}`}
      style={wrapperStyle}
    >
      {/* Shimmer placeholder */}
      {!isLoaded && (
        <div
          className="absolute inset-0 skeleton"
          style={{ zIndex: 1 }}
        />
      )}

      {isInView && (
        <img
          src={src}
          alt={alt || ''}
          className={`transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'} ${className}`}
          style={{ objectFit, ...style }}
          width={width}
          height={height}
          srcSet={srcSet}
          sizes={sizes}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          onLoad={handleLoad}
          onClick={onClick}
          {...(priority ? { fetchpriority: 'high' } : {})}
        />
      )}
    </div>
  );
};

export default OptimizedImage;
